import React from "react";

import isIntuit from "Intuit/isIntuit";
import { intuitBaseUrl } from "Intuit/urls";
import { redirectName } from "QuotePage/urls";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import isSquare from "Square/isSquare";
import { squareBaseUrl } from "Square/urls";
import { formatNameFML } from "Utils/formatName";

import { BodyMedium } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Step, Steps } from "Cobalt/Steps";
import { Div } from "Common/HTML";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import { useGetApplicationQuery, getEmployeeForm, getAdminMode, getProcessOverviewData, getCompanyIDToken } from "./api";
import { getEmployeeHashId } from "./selectors";
import {
  basicInformationURL,
  familyInformationURL,
  chooseMedicalPlanURL,
  chooseDentalPlanURL,
  chooseVisionPlanURL,
  additionalInformationURL,
  signURL,
  isRenewal,
} from "./urls";

import type { EmployeeForm } from "Utils/types";

type StepInfo = {
  status: "past" | "present" | "future";
  url: string;
  innerStepsComplete: boolean;
};

const ExitEmployeeApplicationLink = () => {
  const companyIDToken = useSelector(getCompanyIDToken);
  let partnerBaseUrl = "";

  if (isSquare()) {
    partnerBaseUrl = squareBaseUrl;
  } else if (isIntuit()) {
    partnerBaseUrl = intuitBaseUrl;
  }

  return (
    <Button
      p={0}
      color="link"
      // React Aware routing check for New Enrollment vs. Post enrollment flows
      // Disabled until the state is updated to reflect employees finishing in the Employer Application
      onClick={() => {
        window.location = appendAuthParams(`${partnerBaseUrl}${redirectName}${companyIDToken ? `?company_id_token=${companyIDToken}` : ""}`);
      }}
    >
      Exit Employee Application
    </Button>
  );
};

const TopHeaderSection = (employee: EmployeeForm) => (
  <Div mt={4} mx={4}>
    <Div>You are viewing {formatNameFML(employee)}&apos;s application.</Div>
    <Div>
      <ExitEmployeeApplicationLink />
    </Div>
  </Div>
);

const isStepCompleted = (stepInfo: StepInfo) => stepInfo.status !== "future";

const stepLink = (label: string, stepInfo: StepInfo, isActive: boolean) => {
  const labelWeight = stepInfo.status === "present" ? "bold" : "normal";

  if (!isActive) {
    return (
      <BodyMedium color="muted" weight={labelWeight}>
        {label}
      </BodyMedium>
    );
  }

  return <BodyMedium weight={labelWeight}>{React.createElement(Link as React.ElementType, { to: stepInfo.url }, label)}</BodyMedium>;
};

const stepStatus = (location: string, urls: Array<string>, innerStepsStatus: Array<boolean>): StepInfo => {
  const innerStepsComplete = innerStepsStatus.every((innerStep) => innerStep);

  let status;
  if (urls.includes(location)) {
    status = "present";
  } else if (innerStepsComplete) {
    status = "past";
  } else {
    status = "future";
  }
  const firstFalseIdx = innerStepsStatus.indexOf(false);

  const url = firstFalseIdx === -1 ? urls[0] : urls[firstFalseIdx];

  return {
    status,
    url,
    innerStepsComplete,
  };
};

const useStepDataByPage = (adminMode) => {
  const location = useLocation().pathname;
  const data = useSelector(getProcessOverviewData);
  const employeeHashId = useSelector(getEmployeeHashId);

  return {
    basicInformation: stepStatus(
      location,
      [basicInformationURL(adminMode, isRenewal(), employeeHashId, false), familyInformationURL(adminMode, isRenewal(), employeeHashId, false)],

      [data.basicInformation, data.familyInformation],
    ),

    chooseCoverage: stepStatus(
      location,
      [
        chooseMedicalPlanURL(adminMode, isRenewal(), employeeHashId, false),
        chooseDentalPlanURL(adminMode, isRenewal(), employeeHashId, false),
        chooseVisionPlanURL(adminMode, isRenewal(), employeeHashId, false),
      ],

      [data.chooseMedicalPlan, data.chooseDentalPlan, data.chooseVisionPlan],
    ),

    additionalInformation: stepStatus(location, [additionalInformationURL(adminMode, isRenewal(), employeeHashId, false)], [data.additionalInformation]),

    sign: stepStatus(location, [signURL(adminMode, isRenewal(), employeeHashId, false)], [data.sign]),
  };
};

const ProcessOverview = () => {
  useGetApplicationQuery();
  const employee = useSelector(getEmployeeForm);
  const adminMode = useSelector(getAdminMode) || false;
  const stepData = useStepDataByPage(adminMode);

  return (
    <Div>
      <Steps>
        <Step completed={isStepCompleted(stepData.basicInformation)}>{stepLink("Basic Information", stepData.basicInformation, true)}</Step>
        <Step completed={isStepCompleted(stepData.chooseCoverage)}>
          {stepLink("Choose Plan", stepData.chooseCoverage, stepData.basicInformation.innerStepsComplete)}
        </Step>
        <Step completed={isStepCompleted(stepData.additionalInformation)}>
          {stepLink("Additional Information", stepData.additionalInformation, stepData.chooseCoverage.innerStepsComplete)}
        </Step>
        <Step completed={isStepCompleted(stepData.sign)}>{stepLink("Signature", stepData.sign, stepData.additionalInformation.innerStepsComplete)}</Step>
      </Steps>
      {adminMode && TopHeaderSection(employee)}
    </Div>
  );
};

export default ProcessOverview;
